import { ArrowLeft } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const FatQualityDetail = () => {
  const navigate = useNavigate();

  // Today's fat data
  const totalFat = 65; // total_fat_g
  const saturatedFat = 18; // saturated_fat_g
  const transFat = 2; // trans_fat_g
  const unsaturatedFat = totalFat - saturatedFat - transFat;

  // Calculate fat quality score (lower saturated + trans = better quality)
  const unhealthyFatRatio = ((saturatedFat + transFat) / totalFat) * 100;
  const fatQualityScore = Math.round(100 - unhealthyFatRatio);

  const fatComposition = [
    {
      name: "Unsaturated Fat",
      value: unsaturatedFat,
      color: "hsl(var(--chart-1))",
    },
    {
      name: "Saturated Fat",
      value: saturatedFat,
      color: "hsl(var(--chart-4))",
    },
    { name: "Trans Fat", value: transFat, color: "hsl(var(--destructive))" },
  ];

  const weeklyData = [
    { day: "Mon", saturated: 20, trans: 1, total: 60, quality: 65 },
    { day: "Tue", saturated: 15, trans: 2, total: 55, quality: 69 },
    { day: "Wed", saturated: 22, trans: 3, total: 70, quality: 64 },
    { day: "Thu", saturated: 18, trans: 1, total: 58, quality: 67 },
    { day: "Fri", saturated: 16, trans: 2, total: 62, quality: 71 },
    { day: "Sat", saturated: 25, trans: 4, total: 75, quality: 61 },
    { day: "Sun", saturated: 18, trans: 2, total: 65, quality: 69 },
  ];

  const monthlyData = [
    { week: "Week 1", saturated: 19, trans: 2, total: 62, quality: 66 },
    { week: "Week 2", saturated: 17, trans: 2, total: 60, quality: 68 },
    { week: "Week 3", saturated: 21, trans: 3, total: 68, quality: 65 },
    { week: "Week 4", saturated: 18, trans: 2, total: 65, quality: 69 },
  ];

  // ratio = (saturated + trans) / total
  const unhealthyRatio = (saturatedFat + transFat) / totalFat;

  const getQualityStatus = (ratio: number) => {
    if (ratio <= 0.15) {
      return { status: "Good", color: "text-success" };
    }
    if (ratio <= 0.3) {
      return { status: "Moderate", color: "text-warning" };
    }
    return { status: "Needs Improvement", color: "text-destructive" };
  };

  const status = getQualityStatus(unhealthyRatio);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Fat Quality</h1>
          <div className="w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-chart-1/10 to-chart-1/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Fat Quality
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {unhealthyRatio.toFixed(2)}
            </div>
            <p className={`text-sm font-semibold ${status.color}`}>
              {status.status}
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <div className="text-center">
                <div className="text-xl font-bold text-foreground">
                  {totalFat}g
                </div>
                <p className="text-xs text-muted-foreground">Total Fat</p>
              </div>
              <div className="text-center">
                <div className="text-chart-4 text-xl font-bold">
                  {saturatedFat}g
                </div>
                <p className="text-xs text-muted-foreground">Saturated</p>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-destructive">
                  {transFat}g
                </div>
                <p className="text-xs text-muted-foreground">Trans Fat</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Fat Composition Pie Chart */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 text-center font-bold text-foreground">
            Fat Composition Breakdown
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={fatComposition}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {fatComposition.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                formatter={(value: number) => [`${value}g`, ""]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            {fatComposition.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-muted-foreground">
                  {item.name}: {item.value}g
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* What is Fat Quality */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Fat Quality?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Fat Quality measures the ratio of healthy (unsaturated) fats to
            unhealthy (saturated and trans) fats in your diet. It is calculated
            using your total fat, saturated fat, and trans fat intake. A higher
            score indicates better fat quality, which supports heart health and
            reduces inflammation.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="text-success font-semibold">≤ 0.15</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">0.15 - 0.30</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold text-destructive">Above 0.30</span>
            </div>
          </div>
        </Card>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-2 p-1">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    formatter={(value: number, name: string) => [
                      `${value}g`,
                      name,
                    ]}
                  />
                  <Bar
                    dataKey="saturated"
                    name="Saturated"
                    fill="hsl(var(--chart-4))"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="trans"
                    name="Trans Fat"
                    fill="hsl(var(--destructive))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-2 flex justify-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="bg-chart-4 h-3 w-3 rounded-full" />
                  <span className="text-xs text-muted-foreground">
                    Saturated (g)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-destructive" />
                  <span className="text-xs text-muted-foreground">
                    Trans Fat (g)
                  </span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="quality"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-1))" }}
                    name="Quality Score"
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Tips to Improve Fat Quality
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-1 mt-0.5">•</span>
              <span>Replace butter with olive oil or avocado oil</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-1 mt-0.5">•</span>
              <span>Avoid processed and fried foods high in trans fats</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-1 mt-0.5">•</span>
              <span>
                Choose lean proteins and remove visible fat from meats
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-1 mt-0.5">•</span>
              <span>Add nuts, seeds, and fatty fish to your diet</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default FatQualityDetail;
